// frontend/src/App.jsx
import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate, Outlet } from "react-router-dom";
import Layout from "./components/Layout";
import { MetricsProvider } from "./context/MetricsContext";
import { Web3Provider } from "./context/Web3Context";
import { AuthProvider, useAuth } from "./context/AuthContext";
import "./styles/visualizer-grid.css";
import HomePage from "./pages/HomePage";

import AuthGuard from "./components/AuthGuard";
import InactivityGuard from "./components/InactivityGuard";
import StandbyPage from "./pages/StandbyPage";
import LoginPage from "./pages/LoginPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";

import RealityVerifier from "./components/control-tower/RealityVerifier";

// ✅ 1. Import du Mode Wawa (Favicon Playlist)
import FaviconPlaylist from "./components/FaviconPlaylist";
// --- Pages principales ---
import IcosahedronPage from "./pages/IcosahedronPage";
import CubesPage from "./pages/CubesPage";
import SpiralPage from "./pages/SpiralPage";
import SpiralTorusPage from "./pages/SpiralTorusPage";
import StreamPage from "./pages/StreamPage";
import TorusSpringPage from "./pages/TorusSpringPage";
import CentrifugeLaserPage from "./pages/CentrifugeLaserPage";
import CentrifugeLaserV2Page from "./pages/CentrifugeLaserV2Page";
import CryptoTokenRiverPage from "./pages/CryptoTokenRiverPage";
import MetaCubeOraclePage from "./pages/MetaCubeOraclePage";
import MetaCubeOracleV2Page from "./pages/MetaCubeOracleV2Page";
import MetaCubeOracleV3Page from "./pages/MetaCubeOracleV3Page";
import MetaCubeOracleV4Page from "./pages/MetaCubeOracleV4Page";
import MetaCubeOracleV5Page from "./pages/MetaCubeOracleV5Page";
import MetaCubeQuantiquePage from "./pages/MetaCubeQuantiquePage";
import AdminPage from "./pages/AdminPage";
import MainPage from "./pages/MainPage";
import VisualizerGrid from "./components/VisualizerGrid";
import EntropyDashboardPage from "./pages/EntropyDashboardPage";
import TokenGeneratorDashboardPage from "./pages/TokenGeneratorDashboardPage";
import AdminApiQuota from "./pages/AdminApiQuota";
import AdminSupportView from "./pages/AdminSupportView";
import BlockchainExplorer from "./pages/BlockchainExplorer";
import BlockchainDashboardPage from "./pages/BlockchainDashboardPage";
import Web3WalletPage from "./pages/Web3WalletPage";
// import AdminGraphQLPage from "./pages/AdminGraphQLPage";
import QuantumPass from "./pages/QuantumPass";
import TokenPriceChart from "./pages/TokenPriceChart";
import QuantumIdentityPage from "./pages/QuantumIdentityPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import V2Dashboard from "./pages/V2Dashboard";
import V2Explorer from "./pages/V2Explorer";
import V2Audit from "./pages/V2Audit";
import RetentionPolicyPage from "./pages/RetentionPolicyPage";

import SentinelLayout from "./components/oracle-v2/SentinelLayout";

// --- Portail Client (Phase 19) ---
import { PortalAuthProvider } from "./context/PortalAuthContext";
import PortalGuard from "./components/PortalGuard";
import PortalLogin from "./pages/PortalLogin";
import PortalRegister from "./pages/PortalRegister";
import PortalDashboard from "./pages/PortalDashboard";
import SecurityPasswordPage from "./pages/SecurityPasswordPage";
import PortalPricing from "./pages/PortalPricing";

// --- Portail B2B V1 (Clé d'API) ---
import { B2BProvider } from "./context/B2BContext";
import B2BLogin from "./pages/B2BLogin";
import B2BDashboard from "./pages/B2BDashboard";

import MerkleBatchesView from "./pages/MerkleBatchesView";

// ✅ Forcer un remount à chaque changement de route
function RoutedLayout() {
  const location = useLocation();
  return <Layout key={location.pathname} />;
}

// 🔒 Sécurité : Protection des routes Admin (M26.21 — basé sur AuthContext)
function AdminGuard({ children }) {
  const { isAdmin, authenticated, loading } = useAuth();

  if (loading) return null;

  // Non connecté → retour accueil
  if (!authenticated) return <Navigate to="/" replace />;

  // Connecté mais pas Admin → retour accueil
  if (!isAdmin) return <Navigate to="/" replace />;

  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      {/* ✅ 2. Activation du Mode Wawa (ici pour être actif sur TOUTES les pages) */}
      <FaviconPlaylist />

      <AuthProvider>
      <MetricsProvider>
        <Web3Provider>
          <B2BProvider>
          <Routes>
            {/* ✅ 1. Routes d'Entrée (Publiques, hors Layout) */}
            <Route path="/" element={<StandbyPage />} />
            <Route path="/login" element={<LoginPage />} />

            {/* ✅ 2. Mode Sentinel (V2) - Protégé Admin + Inactivity */}
            <Route path="/v2" element={<AuthGuard><InactivityGuard><AdminGuard><SentinelLayout /></AdminGuard></InactivityGuard></AuthGuard>}>
              <Route path="dashboard" element={<V2Dashboard />} />
              <Route path="explorer" element={<V2Explorer />} />
              <Route path="audit" element={<V2Audit />} />
            </Route>

            <Route path="/quantum-pass" element={<QuantumPass />} />

            {/* ✅ Portail B2B V1 (Standalone, hors Layout principal) */}
            <Route path="/b2b/login" element={<B2BLogin />} />
            <Route path="/b2b/dashboard" element={<B2BDashboard />} />
            <Route path="/b2b/merkle" element={<MerkleBatchesView />} />

            {/* ✅ 3. Routes avec le Layout Principal */}
            <Route element={<RoutedLayout />}>
              
              {/* 💼 Portail Client B2B (Totalement isolé de Google Auth) */}
              <Route path="portal" element={<PortalAuthProvider><Outlet /></PortalAuthProvider>}>
                <Route path="login" element={<PortalLogin />} />
                <Route path="register" element={<PortalRegister />} />
                <Route path="pricing" element={<PortalPricing />} />
                <Route path="dashboard" element={<PortalGuard><PortalDashboard /></PortalGuard>} />
              </Route>

              {/* Pages Légales & Récupération d'Accès */}
              <Route path="privacy" element={<PrivacyPage />} />
              <Route path="terms" element={<TermsPage />} />
              <Route path="retention-policy" element={<RetentionPolicyPage />} />
              <Route path="reset-password" element={<ResetPasswordPage />} />

              {/* Enrôlement du facteur local. Sous AuthGuard : la SESSION
                  GOOGLE est exigée, et la route back-end n'agit que sur
                  `current_user` — un utilisateur ne peut poser un facteur que
                  sur son propre compte. Aucun privilège n'est accordé ici. */}
              <Route path="security/password"
                     element={<AuthGuard><SecurityPasswordPage /></AuthGuard>} />

              {/* 🔒 HUB & MODULES (Protégés par Google Auth + InactivityGuard) */}
              <Route element={<AuthGuard><InactivityGuard><Outlet /></InactivityGuard></AuthGuard>}>
                <Route path="hub" element={<HomePage />} />
                <Route path="hub/api-quota" element={<AdminGuard><AdminApiQuota /></AdminGuard>} />
                
                {/* Pages Protégées Standard */}
                <Route path="quid" element={<QuantumIdentityPage />} />
                <Route path="entropy-dashboard" element={<EntropyDashboardPage />} />
                <Route path="token-generator-dashboard" element={<TokenGeneratorDashboardPage />} />
                <Route path="governance" element={<RealityVerifier />} />
                <Route path="web3-wallet" element={<Web3WalletPage />} />

                {/* Pages Administrateurs */}
                <Route path="admin" element={<AdminGuard><AdminPage /></AdminGuard>} />
                <Route path="admin/support" element={<AdminGuard><AdminSupportView /></AdminGuard>} />
                <Route path="legacy-token-gen" element={<AdminGuard><MainPage /></AdminGuard>} />
                <Route path="icosahedron" element={<AdminGuard><IcosahedronPage /></AdminGuard>} />
                <Route path="cubes" element={<AdminGuard><CubesPage /></AdminGuard>} />
                <Route path="spiral-simple" element={<AdminGuard><SpiralPage /></AdminGuard>} />
                <Route path="spiral-torus" element={<AdminGuard><SpiralTorusPage /></AdminGuard>} />
                <Route path="stream" element={<AdminGuard><StreamPage /></AdminGuard>} />
                <Route path="torus-spring" element={<AdminGuard><TorusSpringPage /></AdminGuard>} />
                <Route path="centrifuge-laser" element={<AdminGuard><CentrifugeLaserPage /></AdminGuard>} />
                <Route path="centrifuge-laser-v2" element={<AdminGuard><CentrifugeLaserV2Page /></AdminGuard>} />
                <Route path="crypto-token-river" element={<AdminGuard><CryptoTokenRiverPage /></AdminGuard>} />
                <Route path="metacube-oracle" element={<AdminGuard><MetaCubeOraclePage /></AdminGuard>} />
                <Route path="metacube-oracle-v2" element={<AdminGuard><MetaCubeOracleV2Page /></AdminGuard>} />
                <Route path="metacube-oracle-v3" element={<AdminGuard><MetaCubeOracleV3Page /></AdminGuard>} />
                <Route path="metacube-oracle-v4" element={<AdminGuard><MetaCubeOracleV4Page /></AdminGuard>} />
                <Route path="metacube-oracle-v5" element={<AdminGuard><MetaCubeOracleV5Page /></AdminGuard>} />
                <Route path="metacube-quantique" element={<AdminGuard><MetaCubeQuantiquePage /></AdminGuard>} />
                <Route path="blockchain" element={<AdminGuard><BlockchainExplorer /></AdminGuard>} />
                <Route path="blockchain-dashboard" element={<AdminGuard><BlockchainDashboardPage /></AdminGuard>} />
                <Route path="token-price" element={<AdminGuard><TokenPriceChart /></AdminGuard>} />
              </Route>

            </Route>
          </Routes>
          </B2BProvider>
        </Web3Provider>
      </MetricsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}